import { z } from 'zod';
import { isPasswordStrong } from './password-policy.js';
// API contracts shared between backend (controllers + DTOs) and frontend
// (forms + clients). Adding a field here automatically widens both sides.
const StrongPassword = z.string().refine(isPasswordStrong, {
    message: 'password rules failed',
});
export const LoginRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
    deviceId: z.string().optional().nullable(),
});
export const ActivateInvitationRequestSchema = z.object({
    invitationToken: z.string().min(16),
    password: StrongPassword,
});
// `refreshToken` is optional in the body: bootstrap requests on page reload
// rely on the httpOnly `jq4_refresh` cookie (per ADR-010 we don't keep the
// refresh token in client-readable storage). Mid-session 401 retries still
// pass the in-memory token explicitly.
export const RefreshRequestSchema = z.object({
    refreshToken: z.string().min(16).optional(),
});
export const LogoutRequestSchema = z.object({
    refreshToken: z.string().min(16),
});
export const RequestPasswordResetRequestSchema = z.object({
    email: z.string().email(),
});
export const ResetPasswordRequestSchema = z.object({
    resetToken: z.string().min(16),
    newPassword: StrongPassword,
});
// MFA (JQ4-4)
export const SetupMfaRequestSchema = z.object({
    mfaToken: z.string().min(16),
});
export const ActivateMfaRequestSchema = z.object({
    mfaToken: z.string().min(16),
    code: z.string().min(6).max(8),
});
export const VerifyMfaRequestSchema = z.object({
    mfaToken: z.string().min(16),
    // 6-digit TOTP or 10-char base64url recovery code
    code: z.string().min(6).max(20),
});
// Email OTP — alternative second factor (email-otp-2fa). Lets a user who
// skipped TOTP enrollment at first login still complete mandatory 2FA via a
// 6-digit code emailed to them.
export const RequestMfaEmailOtpRequestSchema = z.object({
    mfaToken: z.string().min(16),
});
export const VerifyMfaEmailOtpRequestSchema = z.object({
    mfaToken: z.string().min(16),
    code: z.string().regex(/^\d{6}$/, 'code must be a 6-digit number'),
});
// User invitation (JQ4-5)
export const CreateInvitationRequestSchema = z.object({
    email: z.string().email(),
    role: z.enum(['supervisor', 'operator']),
});
export const CreateInvitationResponseSchema = z.object({
    invitationId: z.string().uuid(),
    email: z.string().email(),
    role: z.enum(['supervisor', 'operator']),
    expiresAt: z.string().datetime(),
});
// REQ-C-2 — widened to include org_admin for the super-admin resend path.
// Further widened to include super_admin for the platform-team resend path
// (super_admin resending another super_admin's invitation). Both widenings are
// additive; the tenant resend endpoint (its own controller action) can never
// return org_admin/super_admin so existing consumers are unaffected.
export const ResendInvitationResponseSchema = z.object({
    invitationId: z.string().uuid(),
    email: z.string().email(),
    role: z.enum(['super_admin', 'org_admin', 'supervisor', 'operator']),
    expiresAt: z.string().datetime(),
});
export const InvitationListItemSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    role: z.enum(['super_admin', 'org_admin', 'supervisor', 'operator']),
    status: z.enum(['pending', 'expired', 'used']),
    expiresAt: z.string().datetime(),
    createdAt: z.string().datetime(),
});
export const ListInvitationsResponseSchema = z.object({
    invitations: z.array(InvitationListItemSchema),
});
// ── Platform team — super_admin inviting/managing other super_admins ────────
// Role is implicit (super_admin) and organizationId is always null, so the
// request only carries an email; reuses InvitationListItemSchema for the list
// response (role will always be 'super_admin' in that slice).
export const InvitePlatformAdminRequestSchema = z.object({
    email: z.string().email(),
});
// No `role` field — implicit super_admin, mirrors InviteFirstOrgAdminResponseSchema.
export const InvitePlatformAdminResponseSchema = z.object({
    invitationId: z.string().uuid(),
    email: z.string().email(),
    expiresAt: z.string().datetime(),
});
export const PlatformSuperAdminListItemSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email().nullable(),
    active: z.boolean(),
    lastLoginAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const PlatformSuperAdminListResponseSchema = z.object({
    members: z.array(PlatformSuperAdminListItemSchema),
});
// super-admin-deactivate-users — PATCH /super-admin/team/members/:id toggles
// a platform super_admin between active/inactive (reversible, unlike the
// tenant-scoped DELETE /admin/users/:id which is deactivate-only).
export const SetPlatformAdminActiveRequestSchema = z.object({
    active: z.boolean(),
});
export const SetPlatformAdminActiveResponseSchema = z.object({
    userId: z.string().uuid(),
    active: z.boolean(),
    sessionsRevoked: z.number().int().nonnegative(),
});
// User management (JQ4-6)
const RoleEnum = z.enum(['super_admin', 'org_admin', 'supervisor', 'operator']);
const ManageableRoleEnum = z.enum(['supervisor', 'operator']);
export const ChangeUserRoleRequestSchema = z.object({
    role: ManageableRoleEnum,
});
export const ChangeUserRoleSuperAdminRequestSchema = z.object({
    role: RoleEnum,
});
export const ChangeUserRoleResponseSchema = z.object({
    userId: z.string().uuid(),
    organizationId: z.string().uuid().nullable(),
    previousRole: RoleEnum,
    newRole: RoleEnum,
});
export const DeactivateUserResponseSchema = z.object({
    userId: z.string().uuid(),
    organizationId: z.string().uuid().nullable(),
    sessionsRevoked: z.number().int().nonnegative(),
});
export const ReactivateUserResponseSchema = z.object({
    userId: z.string().uuid(),
    organizationId: z.string().uuid().nullable(),
});
export const UserListItemSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email().nullable(),
    role: RoleEnum,
    active: z.boolean(),
    lastLoginAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const ListUsersResponseSchema = z.object({
    users: z.array(UserListItemSchema),
    total: z.number().int().nonnegative(),
});
export const ListUsersQueryParamsSchema = z.object({
    q: z.string().optional(),
    role: z.enum(['super_admin', 'org_admin', 'supervisor', 'operator']).optional(),
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
});
// Session management (JQ4-8 · US-IAM-007)
export const SessionListItemSchema = z.object({
    id: z.string().uuid(),
    deviceId: z.string().nullable(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    issuedAt: z.string().datetime(),
    lastSeenAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    isCurrent: z.boolean(),
});
export const ListUserSessionsResponseSchema = z.object({
    sessions: z.array(SessionListItemSchema),
});
export const RevokeAllSessionsResponseSchema = z.object({
    sessionsRevoked: z.number().int().nonnegative(),
});
// API response shapes
export const LoginResponseSchema = z.discriminatedUnion('mfaPending', [
    z.object({
        mfaPending: z.literal(false),
        accessToken: z.string(),
        refreshToken: z.string(),
        expiresIn: z.number(),
    }),
    z.object({
        mfaPending: z.literal(true),
        mfaToken: z.string(),
        setupRequired: z.boolean(),
    }),
]);
export const RefreshResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number(),
    // The access JWT carries no email; the refresh response does so a reloaded
    // client can show who is signed in (QA R2-10). Optional for older servers.
    email: z.string().email().nullable().optional(),
});
export const ActivateInvitationResponseSchema = z.object({
    userId: z.string().uuid(),
    // Null for platform-level invitations (super_admin activating another
    // super_admin — no organization scope).
    organizationId: z.string().uuid().nullable(),
    email: z.string().email(),
    mfaSetupRequired: z.boolean(),
    // Additive: role of the activated user (operator = valet -> PWA, not the admin console).
    role: z.enum(['super_admin', 'org_admin', 'supervisor', 'operator']).optional(),
});
export const SetupMfaResponseSchema = z.object({
    otpauthUri: z.string(),
    secret: z.string(),
});
export const ActivateMfaResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number(),
    recoveryCodes: z.array(z.string()),
});
export const VerifyMfaResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number(),
});
export const RequestMfaEmailOtpResponseSchema = z.object({
    sent: z.literal(true),
    expiresInMinutes: z.number(),
});
// Same session shape as VerifyMfaResponseSchema — deliberately NOT reused by
// reference so each MFA path's contract can evolve independently (mirrors
// ActivateMfaResponseSchema's own copy rather than extending VerifyMfa's).
export const VerifyMfaEmailOtpResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number(),
});
//# sourceMappingURL=auth-schemas.js.map